import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Pin, PinOff, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const NoteRow = ({ note, canEdit, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [busy, setBusy] = useState(false);
  const save = async (body) => {
    setBusy(true);
    try { const { data } = await axios.put(`${API}/customers/${note.customer_id}/notes/${note.id}`, body); onChanged(data); setEditing(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update note"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this note?")) return;
    setBusy(true);
    try { await axios.delete(`${API}/customers/${note.customer_id}/notes/${note.id}`); onChanged(null, note.id); toast.success("Note deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete note"); setBusy(false); }
  };
  return (
    <div className={`rounded-lg border p-3 text-sm ${note.pinned ? "border-amber-300 bg-amber-50/60" : "border-slate-200"}`} data-testid={`customer-note-${note.id}`}>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="customer-note-edit-input" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(note.text); }} disabled={busy} data-testid="customer-note-edit-cancel"><X className="w-4 h-4" /></Button>
            <Button size="sm" onClick={() => save({ text })} disabled={busy || !text.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="customer-note-edit-save"><Check className="w-4 h-4 mr-1" /> Save</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="whitespace-pre-wrap text-slate-800" data-testid="customer-note-text">{note.text}</p>
          <div className="flex items-center justify-between mt-2 gap-2">
            <p className="text-xs text-slate-500">
              {note.pinned && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 mr-2 text-[10px]" data-testid="customer-note-pinned-badge">Pinned</Badge>}
              {note.created_by_name || note.created_by} · {when(note.created_at)}{note.updated_at && note.updated_at !== note.created_at ? " · edited" : ""}
            </p>
            {canEdit && note.can_manage && (
              <div className="flex gap-0.5 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" title={note.pinned ? "Unpin" : "Pin to top"} disabled={busy} onClick={() => save({ pinned: !note.pinned })} data-testid="customer-note-pin">{note.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" disabled={busy} onClick={() => setEditing(true)} data-testid="customer-note-edit"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50" title="Delete" disabled={busy} onClick={remove} data-testid="customer-note-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export const CustomerNotes = ({ customerId, canEdit, onCountChange }) => {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/customers/${customerId}/notes`).then((r) => { setNotes(r.data); onCountChange?.(r.data.length); }).catch(() => toast.error("Could not load notes"));
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line
  const add = async () => {
    setBusy(true);
    try { await axios.post(`${API}/customers/${customerId}/notes`, { text }); setText(""); toast.success("Note added"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); } finally { setBusy(false); }
  };
  const changed = (updated, removedId) => {
    if (removedId) { const next = notes.filter((n) => n.id !== removedId); setNotes(next); onCountChange?.(next.length); return; }
    load();
  };
  return (
    <div className="space-y-3" data-testid="customer-notes">
      {canEdit && (
        <div className="space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Internal note (never visible to the customer)…" data-testid="customer-note-input" />
          <div className="flex justify-end">
            <Button size="sm" onClick={add} disabled={busy || !text.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="customer-note-add">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Add note</Button>
          </div>
        </div>
      )}
      {notes === null ? <div className="flex justify-center py-4"><Loader2 className="animate-spin text-slate-400" /></div>
        : notes.length === 0 ? <p className="text-sm text-slate-400" data-testid="customer-notes-empty">No notes yet</p>
        : notes.map((n) => <NoteRow key={n.id} note={n} canEdit={canEdit} onChanged={changed} />)}
    </div>
  );
};
